import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEmail } from '@components/icons/email';
import { IconPhone } from '@components/icons/phone';
import { IconSms } from '@components/icons/phone-sms';
import { IconRedo } from '@components/icons/redo';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useCreateManualReminder } from 'api/reminders/hooks';
import { ReminderSchedule } from 'api/reminders/requests';
import { TravellerJourneyActionDetailed } from 'api/types';

export const RemindersContentDetails = ({
  action,
  emailAction,
  smsAction,
}: {
  action: TravellerJourneyActionDetailed;
  emailAction?: TravellerJourneyActionDetailed<'send_email'>;
  smsAction?: TravellerJourneyActionDetailed<'send_sms'>;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  const formSent = action.status === 'in_progress';
  const formCompleted = action.status === 'completed';

  const reminderData = action.reminder;
  const schedules = action.reminderSchedules ?? [];
  const automaticSchedules = schedules.filter((schedule) => schedule.isAutomatic === true);

  const hasFormCompletedWithNoRemindersSent =
    formCompleted && schedules.every((schedule) => schedule.status !== 'completed');

  const hasNoReminderStarted = schedules.every((schedule) => schedule.status === 'not_started');

  const firstScheduledReminder =
    hasNoReminderStarted && automaticSchedules.length > 0
      ? automaticSchedules.reduce((prev, current) =>
          prev.scheduledDate < current.scheduledDate ? prev : current
        )
      : undefined;
  const sentReminders = schedules.filter(
    (schedule) => schedule.status === 'completed' || schedule.status === 'failed'
  );
  const notStartedReminders = automaticSchedules.filter(
    (schedule) => schedule.status === 'not_started'
  );
  const nextReminder =
    notStartedReminders.length > 0
      ? notStartedReminders.reduce((prev, current) =>
          prev.scheduledDate < current.scheduledDate ? prev : current
        )
      : null;

  const allRemindersSent = sentReminders.length === schedules.length;

  const completedRemindersDates = sentReminders?.map((reminder) => reminder.scheduledDate);
  const uniqueDates = [...new Set(completedRemindersDates)];
  const completedRemindersByDate = uniqueDates.map((date) =>
    sentReminders?.filter((reminder) => reminder.scheduledDate === date)
  );

  const completedAutomaticRemindersByDate = completedRemindersByDate.filter(
    (reminders) => reminders?.[0].isAutomatic
  );
  const completedManualRemindersByDate = completedRemindersByDate.filter(
    (reminders) => !reminders?.[0].isAutomatic
  );

  const getRepeatDescription = () => {
    if (!reminderData?.frequency || !reminderData?.amount) {
      return '';
    }
    if (reminderData.frequency === '1') {
      if (reminderData.amount === '1') {
        return t('repeatRemindersDecsriptionOneOne');
      } else {
        return t('repeatRemindersDecsriptionOneMany', {
          amount: reminderData.amount,
        });
      }
    }
    if (reminderData?.amount === '1') {
      return t('repeatRemindersDecsriptionManyOne', {
        num_days: reminderData.frequency,
      });
    }
    return t('repeatRemindersDecsriptionManyMany', {
      num_days: reminderData.frequency,
      amount: reminderData.amount,
    });
  };

  const createManualReminder = useCreateManualReminder();

  const sendManualReminder = (method: 'email' | 'sms') => {
    const now = new Date();
    const reminderData = {
      travellerAction: action.shortId,
      method: method,
      scheduledDate: now.toISOString(),
      isAutomatic: false,
    };
    createManualReminder.mutateAsync(reminderData);
  };

  return (
    <>
      <Stack space={6}>
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={t('whenFormNotcompleted')} />
            {reminderData ? (
              <Text
                font="regular14"
                text={
                  reminderData.days === '1'
                    ? t('daysAfterSendingDescriptionOne', {
                        hour: reminderData.hours,
                        minute: reminderData.minutes === '0' ? '00' : reminderData.minutes,
                      })
                    : t('daysAfterSendingDescriptionMany', {
                        num_days: reminderData.days,
                        hour: reminderData.hours,
                        minute: reminderData.minutes === '0' ? '00' : reminderData.minutes,
                      })
                }
              />
            ) : (
              <Text font="regular14" text={t('disabled')} color="textHelper" />
            )}
          </Stack>
          {reminderData && (
            <>
              <Stack space={1}>
                <Text font="semibold14" text={t('reminderMethod')} />
                <div>
                  {reminderData.emailNotify && (
                    <Stack orientation="horizontal" space={1} align="center">
                      <IconEmail color="elementPurple50" />
                      <Text font="regular14" text={t('email')} />
                    </Stack>
                  )}
                  {reminderData.smsNotify && (
                    <Stack orientation="horizontal" space={1} align="center">
                      <IconSms color="elementPurple50" />
                      <Text font="regular14" text={t('sms')} />
                    </Stack>
                  )}
                </div>
              </Stack>
            </>
          )}
        </Stack>
        <Separator />
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={t('repeat')} />
            {reminderData?.frequency ? (
              <Text font="regular14" text={getRepeatDescription()} />
            ) : (
              <Text font="regular14" text={t('disabled')} color="textHelper" />
            )}
          </Stack>
        </Stack>
        <Separator />
        <Stack space={4}>
          {hasNoReminderStarted ? (
            <Stack space={4}>
              <div>
                <Text font="semibold14" text={t('sentReminders')} />
                <Text font="regular14" color="textHelper" text={t('noRemindersSent')} />
              </div>
              {firstScheduledReminder && (
                <Text
                  font="regular14"
                  text={t('firstReminder', {
                    date: abbreviateDate(
                      firstScheduledReminder.scheduledDate,
                      'en',
                      'weekday-datetime'
                    ),
                  })}
                />
              )}
            </Stack>
          ) : (
            <Stack space={4}>
              <Text font="semibold14" text={t('sentReminders')} />
              {hasFormCompletedWithNoRemindersSent && (
                <Text
                  font="regular14"
                  color="textHelper"
                  text={t('formCompletedNoRemindersSent')}
                />
              )}
              {completedAutomaticRemindersByDate?.map((reminders, idx) => {
                const emailReminder = reminders?.find((reminder) => reminder.method === 'email');
                const smsReminder = reminders?.find((reminder) => reminder.method === 'sms');
                return (
                  <SentReminders
                    key={idx}
                    emailReminder={emailReminder}
                    smsReminder={smsReminder}
                    idx={idx}
                  />
                );
              })}
              {formSent && reminderData && allRemindersSent && (
                <Stack orientation="horizontal" align="center" space={2}>
                  <IconCheckboxChecked color="green100" />
                  <Text font="regular14" color="green100" text={t('allRemindersSent')} />
                </Stack>
              )}
              {completedManualRemindersByDate?.map((reminders, idx) => {
                const emailReminder = reminders?.find((reminder) => reminder.method === 'email');
                const smsReminder = reminders?.find((reminder) => reminder.method === 'sms');
                return (
                  <SentReminders
                    key={idx}
                    emailReminder={emailReminder}
                    smsReminder={smsReminder}
                    idx={idx}
                    isAutomatic={false}
                  />
                );
              })}
            </Stack>
          )}
          {!firstScheduledReminder && nextReminder && (
            <Text
              font="regular14"
              text={t('nextReminder', {
                date: abbreviateDate(nextReminder?.scheduledDate, 'en', 'weekday-datetime'),
              })}
            />
          )}
        </Stack>
        <Stack align="flex-end">
          <Menu
            buttonProps={{
              text: t('sendReminder'),
              iconLeft: <IconRedo />,
              variant: 'secondary',
              iconRight: <IconDropdown />,
              tooltip: formCompleted ? t('formCompleted') : !formSent ? t('formNotSent') : '',
              disabled: !formSent || formCompleted,
            }}
            placement="bottom"
          >
            <Menu.Item
              label={t('sendsmsReminder')}
              icon={<IconPhone />}
              onSelect={() => sendManualReminder('sms')}
              isDisabled={smsAction?.isDisabled}
              tooltip={smsAction?.isDisabled ? t('noSmsNotification') : ''}
            />
            <Menu.Item
              label={t('sendEmailReminder')}
              icon={<IconEmail />}
              onSelect={() => sendManualReminder('email')}
              isDisabled={emailAction?.isDisabled}
              tooltip={emailAction?.isDisabled ? t('noEmailNotification') : ''}
            />
          </Menu>
        </Stack>
      </Stack>
    </>
  );
};

const SentReminders = ({
  emailReminder,
  smsReminder,
  idx,
  isAutomatic = true,
}: {
  emailReminder?: ReminderSchedule;
  smsReminder?: ReminderSchedule;
  idx: number;
  isAutomatic?: boolean;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  return (
    <Stack key={idx}>
      <Text font="semibold14" text={isAutomatic ? t('automaticReminders') : t('manualReminders')} />
      {emailReminder && <Reminder reminder={emailReminder} type="email" />}
      {smsReminder && <Reminder reminder={smsReminder} type="sms" />}
    </Stack>
  );
};

const Reminder = ({ reminder, type }: { reminder: ReminderSchedule; type: 'email' | 'sms' }) => {
  const { t } = useScopedTranslate(remindersLocale);
  const statusColor = reminder.status === 'completed' ? 'elementGreen200' : 'elementRed100';
  const date = reminder.status === 'completed' ? reminder.dateSucceeded : reminder.dateFailed;

  return date ? (
    <Stack space={2} orientation="horizontal" justify="space-between">
      <Stack orientation="horizontal" align="center">
        <Text
          font="regular14"
          text={type === 'email' ? t('emailSent') : t('smsSent')}
          color="textSecondary"
        />
        <Text font="regular14" text={t(reminder.status)} color={statusColor} />
      </Stack>
      <Text font="regular14" color={statusColor} text={abbreviateDate(date, 'en', 'datetime')} />
    </Stack>
  ) : null;
};

const remindersLocale = {
  en: {
    whenFormNotcompleted: 'When form has not been completed',
    disabled: 'Disabled',
    reminderMethod: 'Reminder method',
    email: 'Email',
    sms: 'SMS',
    daysAfterSendingDescriptionOne:
      'Sends a reminder the day after the form has been sent and has not been completed at {hour}:{minute}',
    daysAfterSendingDescriptionMany:
      'Sends a reminder {num_days} days after form has been sent and has not been completed at {hour}:{minute}',
    repeat: 'Repeat reminders',
    repeatRemindersDecsriptionOneOne:
      'Reminder will only be sent once, the day after the first reminder if form has not been completed',
    repeatRemindersDecsriptionOneMany:
      'Reminders are sent every day after the first reminder if the form has not been completed for a maximum of {amount} times',
    repeatRemindersDecsriptionManyOne:
      'Reminder will be sent once, {num_days} days after the first reminder if the form has not been completed',
    repeatRemindersDecsriptionManyMany:
      'Reminders are sent every {num_days} days after the first reminder if the form has not been completed for a maximum of {amount} times',
    noRemindersSent: 'No reminders have been sent',
    formCompletedNoRemindersSent: 'No reminders were sent because the form has been completed',
    sentReminders: 'Sent reminders',
    sendReminder: 'Send manual reminder',
    sendsmsReminder: 'Send SMS reminder',
    sendEmailReminder: 'Send email reminder',
    formNotSent: 'Form has not been sent',
    noSmsNotification: 'SMS was not selected as a notification method',
    noEmailNotification: 'Email was not selected as a notification method',
    formCompleted: 'Form has been completed',
    firstReminder: 'First automatic reminder is scheduled to be sent at {date}',
    automaticReminders: 'Automatic reminders',
    manualReminders: 'Manual reminders',
    emailSent: 'Email • ',
    smsSent: 'SMS • ',
    not_started: 'Not started',
    in_progress: 'In progress',
    sent: 'Sent',
    completed: 'Delivered',
    failed: 'Delivery failure',
    skipped: 'Skipped',
    nextReminder: 'Next automatic reminder is scheduled to be sent at {date}',
    allRemindersSent: 'All automatic reminders have been sent',
  },
};
